import React, { useMemo } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { Controller, useFormContext } from 'react-hook-form'
import {
  Autocomplete,
  ListItemText,
  ListItem,
  List,
  Box,
  TextField,
} from '@mui/material'
import { FormSection } from '@modules/requirement/requirement-form/form-section.component'
import { Link } from 'react-router-dom'
import { ConnectedAuditRequirement, RequirementFormConfig } from '@shared/types'
import { useAuditQuery } from '../use-audit-query.hook'
import { orderBy } from 'lodash'
import { NO_OPTIONS_PLACEHOLDER } from '@shared/consts'

type Props = {
  editable?: boolean
  companyID: number
  formConfig: RequirementFormConfig
  label: string
}

export const FormConnectedRequirements = ({
  editable,
  companyID,
  formConfig,
  label,
}: Props) => {
  const classes = useStyles()
  const { control, watch, getValues } = useFormContext()

  const { audit, isLoading } = useAuditQuery(
    false,
    companyID,
    Number(formConfig.auditID)
  )

  const data = useMemo(() => {
    return orderBy(
      audit?.scopedRequirements
        ?.flatMap((group) =>
          group.groups.flatMap((group) => group.requirements)
        )
        .map((req) => ({ name: req.name, id: req.id })),
      'name'
    )
  }, [audit])

  if (editable) {
    if (isLoading) {
      return null
    }

    return (
      <Controller
        defaultValue={[]}
        control={control}
        name="connectedAuditRequirements"
        render={(formControllerProps) => {
          return (
            <Autocomplete
              {...formControllerProps}
              value={formControllerProps.field.value
                .map((id: number) => data.find((req) => req.id === id))
                .filter(Boolean)}
              multiple
              className={classes.connectedRequirementsAutocomplete}
              options={data}
              getOptionLabel={(option) => option.name}
              fullWidth
              getOptionKey={(option) => option.id}
              noOptionsText={NO_OPTIONS_PLACEHOLDER}
              filterSelectedOptions
              onChange={(event, values) => {
                const requirementIds = values.map((i) => i.id ?? i)
                const dedupedValues = requirementIds.filter(
                  (value, index) => requirementIds.indexOf(value) === index
                )
                formControllerProps.field.onChange(dedupedValues)
              }}
              renderInput={(params) => <TextField {...params} label={label} />}
            />
          )
        }}
      />
    )
  }

  const connectedAuditRequirements = (watch('connectedAuditRequirements') ||
    []) as ConnectedAuditRequirement[]

  return (
    <FormSection title={label}>
      <Box component="span">
        <List dense className={classes.connectedRequirementsList}>
          {connectedAuditRequirements.map((connectedAuditRequirement) => {
            const { id, name } = connectedAuditRequirement

            return (
              <ListItem key={id} button>
                <Link to={`/company-profile/${companyID}/requirement/${id}`}>
                  <ListItemText primary={name} />
                </Link>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </FormSection>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    connectedRequirementsAutocomplete: {
      marginTop: theme.spacing(2),
    },
    connectedRequirementsList: {
      paddingTop: 0,
    },
  }
})
